import React from 'react';
import {
  Box,
  Select,
  MenuItem,
  Typography,
  useTheme,
  SelectChangeEvent,
} from '@mui/material';
import { getFlatPortfolioColors, portfolioTypography } from '../../../utils/getFlatPortfolioColors';

export interface PageSizeSelectorProps {
  pageSize: number;
  onPageSizeChange: (pageSize: number) => void;
  totalItems: number;
  options?: number[];
  showLabel?: boolean;
}

const DEFAULT_OPTIONS = [10, 25, 50, 100];

const PageSizeSelector: React.FC<PageSizeSelectorProps> = ({
  pageSize,
  onPageSizeChange,
  totalItems,
  options = DEFAULT_OPTIONS,
  showLabel = true,
}) => {
  const theme = useTheme();
  const colors = getFlatPortfolioColors(theme);

  const handleChange = (event: SelectChangeEvent<number>) => {
    const value = event.target.value as number;
    onPageSizeChange(value);
  };

  // Filter options to only show those that make sense for the dataset
  const relevantOptions = options.filter(option => option <= totalItems || option === pageSize);

  // If current pageSize is not in options, add it
  if (!relevantOptions.includes(pageSize)) {
    relevantOptions.push(pageSize);
    relevantOptions.sort((a, b) => a - b);
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {showLabel && (
        <Typography
          sx={{
            ...portfolioTypography.caption,
            color: colors.text.secondary,
            fontWeight: 500,
          }}
        >
          Show
        </Typography>
      )}

      <Select
        value={pageSize}
        onChange={handleChange}
        size="small"
        sx={{
          minWidth: 70,
          height: 32,
          backgroundColor: colors.background.secondary,
          border: `1px solid ${colors.border.primary}`,
          borderRadius: 0,

          '& .MuiSelect-select': {
            ...portfolioTypography.caption,
            color: colors.text.primary,
            fontWeight: 500,
            py: 0.5,
            px: 1,
          },

          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },

          '&:hover': {
            backgroundColor: colors.background.tertiary,
            borderColor: colors.border.secondary,
          },

          '&.Mui-focused': {
            borderColor: colors.functional.success,
            backgroundColor: colors.background.secondary,
          },

          '& .MuiSelect-icon': {
            color: colors.text.secondary,
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              backgroundColor: colors.background.secondary,
              border: `1px solid ${colors.border.primary}`,
              borderRadius: 0,
              mt: 0.5,
              boxShadow: 'none',

              '& .MuiMenuItem-root': {
                ...portfolioTypography.caption,
                color: colors.text.primary,
                py: 1,
                px: 1.5,
                minHeight: 'auto',

                '&:hover': {
                  backgroundColor: colors.background.tertiary,
                },

                '&.Mui-selected': {
                  backgroundColor: colors.background.tertiary,
                  color: colors.functional.success,
                  fontWeight: 600,
                },
              },
            },
          },
        }}
      >
        {relevantOptions.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>

      {showLabel && (
        <Typography
          sx={{
            ...portfolioTypography.caption,
            color: colors.text.secondary,
            fontWeight: 500,
          }}
        >
          per page
        </Typography>
      )}
    </Box>
  );
};

export default PageSizeSelector;
